import { ThreeEvent, useFrame } from '@react-three/fiber'
import { SiteViewFeatureGenerationConfig } from 'features/overview'
import { useFeatureInteractionContext } from 'features/siteMap/contexts/FeatureInteractionContext'
import { TilesetMapFeatureDispatch } from 'features/siteMap/tilesetMap/features/TilesetMapFeatureDispatch'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import { UnitDisplayPropertiesFn } from 'features/siteMapViz'
import isNil from 'lodash/isNil'
import React from 'react'
import { Vector3 } from 'three'

const zero = new Vector3(0, 0, 0)

interface TilesetMapRaycastIndicatorProps {
  getUnitDisplayProperties: UnitDisplayPropertiesFn
  getUnitDisplayName: (unitID: string) => string | undefined
}

export const TilesetMapRaycastIndicator: React.FC<
  TilesetMapRaycastIndicatorProps
> = (props) => {
  const {
    siteViewFeatureGenerationConfig,
    setSiteViewFeatureGenerationConfig,
  } = useFeatureInteractionContext()
  const { convertPositionToCartographic } = useTilesetMapFeatureHelpers()

  useFrame((state) => {
    state.raycaster.setFromCamera(state.pointer, state.camera)

    setSiteViewFeatureGenerationConfig(
      (prevConfig: SiteViewFeatureGenerationConfig | undefined) => {
        if (!prevConfig) return prevConfig

        const pos =
          state.raycaster
            .intersectObjects(state.scene.children, true)
            ?.find((hit) => hit.object.name !== prevConfig.primitive.id)
            ?.point ?? zero

        return {
          ...prevConfig,
          primitive: {
            ...prevConfig.primitive,
            coordinate: convertPositionToCartographic(pos)!,
          },
        }
      }
    )
  })

  return !siteViewFeatureGenerationConfig ? null : (
    <group
      onClick={(e: ThreeEvent<MouseEvent>) => {
        // TODO: don't fire callback when map has been dragged
        if (siteViewFeatureGenerationConfig.clickCallback) {
          siteViewFeatureGenerationConfig.clickCallback(
            e,
            siteViewFeatureGenerationConfig.primitive.coordinate
          )
        }
      }}>
      <TilesetMapFeatureDispatch
        feature={siteViewFeatureGenerationConfig.primitive}
        featureDisplayName={
          isNil(siteViewFeatureGenerationConfig.primitive.unit_id)
            ? undefined
            : props.getUnitDisplayName(
                siteViewFeatureGenerationConfig.primitive.unit_id
              )
        }
        key='raycast'
        visible={true}
        getUnitDisplayProperties={props.getUnitDisplayProperties}
        getExtraLabelDisplay={() => null}
      />
    </group>
  )
}
